import React from 'react';
import styles from './ImageCardBlock.module.scss';
import { ImageCardBlockFragment } from '@src/graphql/gql-types';
import { Container } from '@src/elements/Grid';
import ImageCard from '../ImageCard';
import RichTextRender from '../RichTextRender';
import { RICHTEXT_RENDER_MODE } from '@src/types/richText';
import { ContentfulPageTypes } from '@src/types/contentful';
import CTAButton from '../Button/CTAButton';
import {
  COLOR_LIME,
  COLOR_SIDECAR,
  COLOR_SIDECAR_LIGHTER,
  COLOR_WHITE
} from '@src/types/colors';
import { SunraySVG } from '@src/svgs';
import classNames from 'classnames';

type ImageCardBlock = ImageCardBlockFragment & {
  pageType: ContentfulPageTypes;
};

const ImageCardBlock: React.FC<ImageCardBlock> = ({
  tagline,
  layout,
  showSunray,
  mainHeading,
  bodyContent,
  primaryCta,
  secondaryCta,
  imageCardItemsCollection,
  pageType
}) => {
  return (
    <div className={styles.imageCardBlock}>
      <Container>
        <div className={styles.imageCardBlockBody}>
          {showSunray && (
            <div className={styles.sunrayContainer}>
              <SunraySVG color={COLOR_LIME} />
            </div>
          )}
          <div
            className={classNames(styles.imageCardBlockContent, {
              [styles.centerContent]: layout === 'center'
            })}
          >
            {tagline && <p className={styles.tagline}>{tagline}</p>}
            <div className={styles.mainHeading}>
              {mainHeading && (
                <RichTextRender
                  bodyContent={mainHeading}
                  pageType={pageType}
                  renderMode={RICHTEXT_RENDER_MODE.STYLED_HEADING}
                  tagOverride="h2"
                />
              )}
            </div>
            {bodyContent && (
              <div className={styles.bodyContent}>
                <RichTextRender
                  bodyContent={bodyContent}
                  pageType={pageType}
                  renderMode={RICHTEXT_RENDER_MODE.NO_BLOCK_EMBEDS}
                />
              </div>
            )}
            {(primaryCta || secondaryCta) && (
              <div className={styles.ctaContainer}>
                {primaryCta && (
                  <CTAButton
                    cta={primaryCta}
                    label={primaryCta.label || ''}
                    boxIcon="right-arrow-alt"
                    iconPosition="right"
                    color={COLOR_SIDECAR}
                  />
                )}
                {secondaryCta && (
                  <CTAButton
                    color={COLOR_WHITE}
                    fillVariant="outline"
                    cta={secondaryCta}
                    label={secondaryCta.label || ''}
                    boxIcon="right-arrow-alt"
                    iconPosition="right"
                  />
                )}
              </div>
            )}
          </div>
          <div className={styles.imageCardItemsContainer}>
            {imageCardItemsCollection?.items.map((item, index) => (
              <React.Fragment key={`${item?.heading}-${index}`}>
                {item && (
                  <ImageCard {...item} background={COLOR_SIDECAR_LIGHTER} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ImageCardBlock;
